import ApiService from "@/core/services/ApiService";
import { Actions, Mutations } from "@/store/enums/StoreEnums";
import { Module, Action, Mutation, VuexModule } from "vuex-module-decorators";

// 商城优惠券接口
export interface MallCoupon {
  id: number;
  name: string;
  coupon_type: string;
  coupon_type_text: string;
  discount_value: number;
  discount_text: string;
  min_amount: number;
  max_discount: number | null;
  total_quantity: number | null;
  claimed_quantity: number;
  per_user_limit: number | null;
  start_time: string | null;
  end_time: string | null;
  validity_days: number | null;
  status: string;
  allow_user_claim: boolean;
  product_scope: string;
  product_ids: number[];
  return_days: number;
  created_at: string;
  updated_at: string;
  stats?: {
    total_claimed: number;
    total_used: number;
    total_expired: number;
    total_unused: number;
    total_locked: number;
    total_returned: number;
  };
}

// 用户持有优惠券接口
export interface UserCouponItem {
  id: number;
  user_id: number;
  user_nickname: string;
  user_mobile: string;
  status: string;
  status_text: string;
  expire_at: string | null;
  locked_at: string | null;
  used_at: string | null;
  returned_at: string | null;
  return_reason: string | null;
  created_at: string;
}

// 优惠券使用情况接口
export interface MallCouponUsage {
  user_coupons: Array<UserCouponItem>;
  total: number;
  stats: {
    total_claimed: number;
    total_used: number;
    total_expired: number;
    total_unused: number;
    total_locked: number;
    total_returned: number;
  };
}

@Module
export default class MallCouponModule extends VuexModule {
  mall_coupons = [] as Array<MallCoupon>;
  mall_coupon_count = 0;
  mall_coupon = {} as MallCoupon;
  mall_coupon_usage = {} as MallCouponUsage;

  get currentMallCoupon(): MallCoupon {
    return this.mall_coupon;
  }

  get currentMallCoupons(): Array<MallCoupon> {
    return this.mall_coupons;
  }

  get currentMallCouponCount() {
    return this.mall_coupon_count;
  }

  get currentMallCouponUsage(): MallCouponUsage {
    return this.mall_coupon_usage;
  }

  @Mutation
  [Mutations.SET_MALL_COUPONS](mall_coupons) {
    this.mall_coupons = mall_coupons;
  }

  @Mutation
  [Mutations.SET_MALL_COUPON](mall_coupon) {
    this.mall_coupon = mall_coupon;
  }

  @Mutation
  [Mutations.SET_MALL_COUPON_COUNT](mall_coupon_count) {
    this.mall_coupon_count = mall_coupon_count;
  }

  @Mutation
  [Mutations.SET_MALL_COUPON_USAGE](mall_coupon_usage) {
    this.mall_coupon_usage = mall_coupon_usage;
  }

  @Action
  [Actions.GET_MALL_COUPONS](params) {
    ApiService.setHeader();

    let queryString = "?";

    if (params) {
      Object.keys(params).forEach((i) => {
        if (params[i] !== undefined && params[i] !== null && params[i] !== '') {
          queryString += "&" + i + "=" + params[i];
        }
      });
    }

    return new Promise<void>((resolve, reject) => {
      ApiService.get("admin/coupons", queryString)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_MALL_COUPONS, data["coupons"]);
          this.context.commit(Mutations.SET_MALL_COUPON_COUNT, data["total"]);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject();
        });
    });
  }

  @Action
  [Actions.GET_MALL_COUPON](id) {
    ApiService.setHeader();

    return new Promise<void>((resolve, reject) => {
      ApiService.get(`admin/coupons/${id}`)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_MALL_COUPON, data["coupon"]);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject();
        });
    });
  }

  @Action
  [Actions.CREATE_MALL_COUPON](params) {
    ApiService.setHeader();

    return new Promise<void>((resolve, reject) => {
      ApiService.post("admin/coupons", params)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_MALL_COUPON, data["coupon"]);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject();
        });
    });
  }

  @Action
  [Actions.UPDATE_MALL_COUPON](credentials) {
    ApiService.setHeader();

    return new Promise<void>((resolve, reject) => {
      ApiService.put(`admin/coupons/${credentials.id}`, credentials.values)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_MALL_COUPON, data["coupon"]);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject();
        });
    });
  }

  @Action
  [Actions.DELETE_MALL_COUPON](id) {
    ApiService.setHeader();

    return new Promise<void>((resolve, reject) => {
      ApiService.delete(`admin/coupons/${id}`)
        .then(() => {
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject();
        });
    });
  }

  @Action
  [Actions.GRANT_MALL_COUPON](params) {
    ApiService.setHeader();

    // 支持 user_ids 或 mobiles 参数
    const requestParams: { user_ids?: number[]; mobiles?: string[] } = {};
    if (params.user_ids && params.user_ids.length > 0) {
      requestParams.user_ids = params.user_ids;
    }
    if (params.mobiles && params.mobiles.length > 0) {
      requestParams.mobiles = params.mobiles;
    }

    return new Promise<any>((resolve, reject) => {
      ApiService.post(`admin/coupons/${params.id}/grant`, requestParams)
        .then(({ data }) => {
          resolve(data);
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject();
        });
    });
  }

  @Action
  [Actions.GET_MALL_COUPON_USAGE](params) {
    ApiService.setHeader();

    let queryString = "?";

    if (params) {
      Object.keys(params).forEach((i) => {
        if (i !== 'id' && params[i] !== undefined && params[i] !== null && params[i] !== '') {
          queryString += "&" + i + "=" + params[i];
        }
      });
    }

    return new Promise<void>((resolve, reject) => {
      ApiService.get(`admin/coupons/${params.id}/usage`, queryString)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_MALL_COUPON_USAGE, {
            user_coupons: data["user_coupons"],
            total: data["total"],
            stats: data["stats"]
          });
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject();
        });
    });
  }
}
